import {Button, Dropdown, DropdownItem, showNotification} from 'platform/components';
import {Hide, Show, Text} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {useParams} from 'react-router-dom';

import {isArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {InspectionType, useInspectionListQuery, useListThemeQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, usePermissions} from '@dms/shared';

import {useGetOrCreateInspection} from './hooks/useGetOrCreateInspection';
import {useWorkspaceLanguage} from './hooks/useWorkspaceLanguage';
import {openDigitalCertificate} from './utils/openDigitalCertificate';

const DATA_TEST_ID = 'digital-cert-link';

interface DigitalCertificateLinkProps {
  inspectionId?: string | Nullish;
  isDisabled?: boolean;
  label?: string;
  showEmptyPlaceholderWithoutPermission?: boolean;
  variant?: 'outlined' | 'link';
}

export const DigitalCertificateLink = (props: DigitalCertificateLinkProps) => {
  const {id: vehicleId} = useParams();
  const workspaceLanguage = useWorkspaceLanguage();
  const [canShareCarAuditData] = usePermissions({permissionKeys: ['carAuditShareLink']});
  const getOrCreateInspection = useGetOrCreateInspection();
  const {data: themesData, isLoading: isLoadingThemes} = useListThemeQuery();
  const {data: vehicleInspections} = useInspectionListQuery(
    {
      inspectionListRequestBody: {
        vehicleId: vehicleId!, // `vehicleId` is checked in second argument of `useInspectionListQuery`
        inspectionType: InspectionType.VEHICLE_CONDITION,
      },
    },
    {skip: isNotNilOrEmpty(props.inspectionId) || isNilOrEmpty(vehicleId)}
  );

  const themes = isArray(themesData) ? themesData : [];

  // Either specific inspection passed as a prop or the vehicle condition inspection
  const inspectionId = props.inspectionId || vehicleInspections?.[0]?.id;
  const hasMultipleThemes = themes.length > 1;

  const onShareLink = async (themeId?: string) => {
    const existingInspectionId = await getOrCreateInspection(vehicleId, inspectionId).catch(() => {
      showNotification.error();
      return undefined;
    });

    if (!existingInspectionId) {
      return;
    }

    openDigitalCertificate({
      inspectionId: existingInspectionId,
      language: workspaceLanguage,
      themeId,
    });
  };

  const buttonTitle = props.label ?? i18n.t('page.advertisement.labels.digitalCertificate');
  const buttonVariant = props.variant ?? 'outlined';
  const isButtonDisabled = isNilOrEmpty(inspectionId) || props.isDisabled;

  return (
    <>
      <Show when={!canShareCarAuditData && props.showEmptyPlaceholderWithoutPermission}>
        <Text size="small">{EMPTY_PLACEHOLDER}</Text>
      </Show>
      <Show when={canShareCarAuditData}>
        <Hide when={hasMultipleThemes}>
          <Button
            title={buttonTitle}
            variant={buttonVariant}
            isLoading={isLoadingThemes}
            isDisabled={isButtonDisabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const singleTheme = themes[0];
              if (singleTheme) {
                return onShareLink(singleTheme.themeId);
              }

              return onShareLink();
            }}
            data-testid={DATA_TEST_ID}
          />
        </Hide>
        <Show when={hasMultipleThemes}>
          <Dropdown
            dropdownControl={
              <Button
                title={buttonTitle}
                variant={buttonVariant}
                rightIcon="hardware/keyboard_arrow_down"
                data-testid={DATA_TEST_ID}
                isDisabled={isButtonDisabled}
              />
            }
            isDisabled={isButtonDisabled}
            onButtonClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            data-testid={DATA_TEST_ID}
          >
            {themes.map((theme) => (
              <DropdownItem
                key={theme.themeId}
                label={theme.name}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShareLink(theme.themeId);
                }}
              />
            ))}
          </Dropdown>
        </Show>
      </Show>
    </>
  );
};
